import React, { useState, useEffect, useRef } from 'react';
import {
  X, Users, Search, Plus, Calendar, Briefcase, FileText,
  ChevronDown, Cpu, Monitor, MapPin, Loader2, Flag, UserCheck
} from 'lucide-react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { toast } from 'react-hot-toast';
import ModalOverlay from '../../../components/ui/ModalOverlay';
import { userService } from '../../users';
import { resourceService } from '../../resources';
import { BRAND_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ─── helpers ────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = ['#37559e','#bf6f50','#15803d','#b45309','#1d4ed8','#7c3aed','#0891b2','#be123c'];
const getInitials = (name = '') =>
  name.trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
const getAvatarColor = (name = '') =>
  AVATAR_PALETTE[(name.charCodeAt(0) ?? 0) % AVATAR_PALETTE.length];

const PROFICIENCY_CLS = {
  beginner:     'bg-blue-50 text-blue-700',
  intermediate: 'bg-emerald-50 text-emerald-700',
  advanced:     'bg-violet-50 text-violet-700',
  expert:       'bg-orange-50 text-orange-700',
};

const RESOURCE_ICON = { hardware: Cpu, software: Monitor, venue: MapPin };

// ─── SectionHeader ──────────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, label, count }) => (
  <div className="flex items-center gap-2 pb-2 mb-3 sm:mb-4 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
    <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-md" style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
      <Icon size={11} style={{ color: BRAND_COLORS.primary }} />
    </span>
    <span className="text-xs sm:text-sm font-semibold" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</span>
    {count > 0 && (
      <span className="ml-auto text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted, color: BRAND_COLORS.primary }}>
        {count}
      </span>
    )}
  </div>
);

// ─── Input / Textarea / Select shared styles ────────────────────────────────

const inputCls = (err) =>
  `w-full px-2.5 py-2 sm:px-3 sm:py-2.5 rounded-xl border text-sm outline-none transition-all
   focus:ring-2 focus:ring-[#37559e]/20 focus:border-[#37559e]
   ${err ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'}`;

// ─── TeamMemberSelect ────────────────────────────────────────────────────────

const TeamMemberSelect = ({ members, selected, onChange }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = members.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.role?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (member) => {
    const exists = selected.some(m => m.id === member.id);
    onChange(exists ? selected.filter(m => m.id !== member.id) : [...selected, member]);
  };

  return (
    <div ref={ref} className="relative">
      {/* Selected chips */}
      <div
        onClick={() => setOpen(o => !o)}
        className="min-h-[44px] w-full px-3 py-2 rounded-xl border border-gray-200 bg-white hover:border-gray-300 cursor-pointer flex flex-wrap gap-1.5 items-center"
      >
        {selected.length === 0 ? (
          <span className="text-sm text-gray-400">Select team members…</span>
        ) : (
          selected.map(m => (
            <span
              key={m.id}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: getAvatarColor(m.name) }}
            >
              {m.name}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(m); }}
                className="opacity-70 hover:opacity-100"
              >
                <X size={10} />
              </button>
            </span>
          ))
        )}
        <ChevronDown size={14} className="ml-auto text-gray-400 flex-shrink-0" />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-20 mt-1.5 w-full rounded-xl border bg-white shadow-xl overflow-hidden" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="p-2 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-50">
              <Search size={13} className="text-gray-400" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or role…"
                className="w-full text-sm bg-transparent outline-none text-gray-700"
                onClick={e => e.stopPropagation()}
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-center text-gray-400">No members found</li>
            ) : (
              filtered.map(m => {
                const isSelected = selected.some(s => s.id === m.id);
                return (
                  <li
                    key={m.id}
                    onClick={() => toggle(m)}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <div
                      className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold"
                      style={{ backgroundColor: getAvatarColor(m.name) }}
                    >
                      {getInitials(m.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{m.name}</p>
                      <p className="text-xs text-gray-400 truncate">{m.role}</p>
                    </div>
                    {m.skills?.slice(0, 2).map((skill, i) => {
                      const name = typeof skill === 'string' ? skill : skill.name;
                      const prof = typeof skill === 'string' ? null : skill.proficiency;
                      return (
                        <span key={i} className={`hidden sm:inline text-[10px] px-1.5 py-0.5 rounded-full ${PROFICIENCY_CLS[prof] || 'bg-gray-100 text-gray-600'}`}>
                          {name}
                        </span>
                      );
                    })}
                    {isSelected && (
                      <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: BRAND_COLORS.primary }}>
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2.5 2.5L7 1.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </span>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── ProjectModal ────────────────────────────────────────────────────────────

const ProjectModal = ({ isOpen, onClose, project, onSave }) => {
  const { user } = useApiAuth();
  const [formData, setFormData] = useState({
    name: '', description: '', status: 'planning',
    startDate: '', endDate: '', team: [], projectManager: null, milestones: [], resources: []
  });
  const [dateError, setDateError] = useState('');
  const [availableTeamMembers, setAvailableTeamMembers] = useState([]);
  const [availableResources, setAvailableResources] = useState({ hardware: [], software: [], venue: [] });
  const [milestone, setMilestone] = useState({ title: '', description: '', dueDate: '', overseer: '' });
  const [resource, setResource] = useState({ id: '', name: '', type: 'hardware', quantity: 1, status: 'available' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!isOpen || !user) return;
    setLoadingUsers(true);
    userService.getActiveUsers()
      .then(res => {
        const users = Array.isArray(res) ? res : (res?.users || []);
        setAvailableTeamMembers(users.map(u => ({ id: u.id, name: u.name, role: u.role, email: u.email, skills: u.skills || [] })));
      })
      .catch(() => toast.error('Failed to load team members'))
      .finally(() => setLoadingUsers(false));
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;
    resourceService.getResources()
      .then(res => {
        const all = Array.isArray(res) ? res : (res?.resources || []);
        const normalized = all.map(r => {
          const src = String(r.type || r.category || '').toLowerCase();
          const type = src === 'room' || src === 'venue' ? 'venue' : src === 'software' ? 'software' : 'hardware';
          const availableQuantity = r.availableQuantity ?? r.available_quantity ?? r.status_counts?.available ?? r.quantity ?? 0;
          return { ...r, type, availableQuantity };
        });
        setAvailableResources({
          hardware: normalized.filter(r => r.type === 'hardware' && r.availableQuantity > 0),
          software: normalized.filter(r => r.type === 'software' && r.availableQuantity > 0),
          venue:    normalized.filter(r => r.type === 'venue'    && r.availableQuantity > 0),
        });
      })
      .catch(() => toast.error('Failed to load resources'));
  }, [isOpen]);

  useEffect(() => {
    if (project) {
      setFormData({
        ...project,
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate:   project.endDate   ? new Date(project.endDate).toISOString().split('T')[0]   : '',
      });
    } else {
      setFormData({ name: '', description: '', status: 'planning', startDate: '', endDate: '', team: [], projectManager: null, milestones: [], resources: [] });
      setDateError('');
    }
  }, [project, isOpen]);

  const validateDates = (start, end) => {
    if (!start || !end) { setDateError('Both dates are required'); return false; }
    if (new Date(end) <= new Date(start)) { setDateError('End date must be after start date'); return false; }
    setDateError(''); return true;
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };
    if (name === 'startDate' || name === 'endDate') validateDates(next.startDate, next.endDate);
    setFormData(next);
  };

  const addMilestone = () => {
    if (!milestone.title || !milestone.dueDate || !milestone.overseer) {
      toast.error('Fill in all milestone fields');
      return;
    }
    const overseerDetails = formData.team.find(m => m.id === milestone.overseer);
    setFormData(prev => ({
      ...prev,
      milestones: [...prev.milestones, { ...milestone, id: Date.now().toString(), overseerName: overseerDetails?.name || 'Unknown' }]
    }));
    setMilestone({ title: '', description: '', dueDate: '', overseer: '' });
  };

  const addResource = () => {
    if (!resource.id || !resource.quantity) { toast.error('Select a resource and quantity'); return; }
    const sel = availableResources[resource.type].find(r => r.id === resource.id);
    if (!sel) { toast.error('Select a valid resource'); return; }
    if (sel.availableQuantity < resource.quantity) { toast.error(`Only ${sel.availableQuantity} available`); return; }
    setFormData(prev => ({ ...prev, resources: [...prev.resources, { ...resource, name: sel.name, uniqueId: Date.now().toString() }] }));
    setResource({ id: '', name: '', type: 'hardware', quantity: 1, status: 'available' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateDates(formData.startDate, formData.endDate)) return;
    const hasInvalidMilestones = formData.milestones?.some(m => new Date(m.dueDate) > new Date(formData.endDate));
    if (hasInvalidMilestones) { toast.error('Milestone dates cannot be after project end date'); return; }

    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        milestones: formData.milestones?.map(m => ({ title: m.title, description: m.description || '', dueDate: m.dueDate, status: 'pending', progress: 0 })) || [],
        status: formData.status || 'active',
      });
      onClose();
    } catch {
      toast.error('Failed to save project');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ResIcon = RESOURCE_ICON[resource.type] || Cpu;

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose}>
      {/* Responsive container: full-screen on mobile, constrained on larger screens */}
      <div className="bg-white flex flex-col shadow-2xl overflow-hidden
                      w-full h-full rounded-none
                      sm:rounded-2xl sm:h-auto sm:max-h-[92dvh] sm:w-[90vw] sm:max-w-2xl
                      lg:max-w-3xl">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 flex-shrink-0" style={{ backgroundColor: BRAND_COLORS.primary }}>
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <Briefcase size={13} className="text-white sm:hidden" />
              <Briefcase size={15} className="text-white hidden sm:block" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-semibold text-white leading-tight">
                {project ? 'Edit Project' : 'New Project'}
              </h2>
              <p className="text-[10px] sm:text-xs text-white/70">
                {project ? 'Update project details' : 'Fill in the details to get started'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X size={15} className="text-white" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 px-4 py-4 sm:px-6 sm:py-5 space-y-5 sm:space-y-7">
          <form id="project-form" onSubmit={handleSubmit}>

            {/* ── Basics ── */}
            <section className="space-y-3 sm:space-y-4">
              <SectionHeader icon={FileText} label="Basic Information" />

              <div>
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
                  Project Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Website Redesign 2025"
                  className={inputCls(false)}
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">Status</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value })}
                  className={inputCls(false)}
                >
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Briefly describe the project goals and scope…"
                  className={inputCls(false)}
                  required
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
                    <span className="flex items-center gap-1"><Calendar size={10} /> Start <span className="text-red-400">*</span></span>
                  </label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleDateChange} className={inputCls(!!dateError)} required />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
                    <span className="flex items-center gap-1"><Calendar size={10} /> End <span className="text-red-400">*</span></span>
                  </label>
                  <input type="date" name="endDate" value={formData.endDate} onChange={handleDateChange} min={formData.startDate || undefined} className={inputCls(!!dateError)} required />
                </div>
              </div>
              {dateError && <p className="text-xs text-red-500">{dateError}</p>}
            </section>

            {/* ── Team + Project Manager ── */}
            <section className="space-y-3 mt-5 sm:mt-7">
              <SectionHeader icon={Users} label="Team Members" count={formData.team.length} />
              {loadingUsers ? (
                <div className="flex items-center gap-2 py-3 text-xs sm:text-sm text-gray-400">
                  <Loader2 size={14} className="animate-spin" /> Loading members…
                </div>
              ) : (
                <TeamMemberSelect members={availableTeamMembers} selected={formData.team} onChange={team => setFormData({ ...formData, team })} />
              )}
              {formData.team.length > 0 && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 pt-1">
                  {formData.team.map(m => (
                    <div key={m.id} className="flex items-center gap-1.5 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-lg border text-xs" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                      <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-white text-[8px] sm:text-[9px] font-bold flex-shrink-0" style={{ backgroundColor: getAvatarColor(m.name) }}>
                        {getInitials(m.name)}
                      </div>
                      <span className="font-medium text-gray-700">{m.name}</span>
                      <span className="hidden sm:inline text-gray-400">{m.role}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Project Manager — placed here so team is selected first */}
              <div className="pt-2 sm:pt-3">
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 sm:mb-1.5 uppercase tracking-wide">
                  <span className="flex items-center gap-1"><UserCheck size={10} /> Project Manager</span>
                </label>
                <select
                  value={formData.projectManager || ''}
                  onChange={e => setFormData({ ...formData, projectManager: e.target.value })}
                  className={inputCls(false)}
                  disabled={formData.team.length === 0}
                >
                  <option value="">{formData.team.length === 0 ? '— Add team members first —' : '— Select project manager —'}</option>
                  {formData.team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </section>

            {/* ── Milestones ── */}
            <section className="space-y-3 sm:space-y-4 mt-5 sm:mt-7">
              <SectionHeader icon={Flag} label="Milestones" count={formData.milestones.length} />

              <div className="rounded-xl border p-3 sm:p-4 space-y-3 bg-gray-50/60" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <input
                    type="text"
                    placeholder="Milestone title"
                    value={milestone.title}
                    onChange={e => setMilestone({ ...milestone, title: e.target.value })}
                    className={inputCls(false)}
                  />
                  <input
                    type="date"
                    value={milestone.dueDate}
                    onChange={e => setMilestone({ ...milestone, dueDate: e.target.value })}
                    min={formData.startDate || undefined}
                    max={formData.endDate || undefined}
                    className={inputCls(false)}
                  />
                </div>
                <textarea
                  placeholder="Milestone description (optional)"
                  value={milestone.description}
                  onChange={e => setMilestone({ ...milestone, description: e.target.value })}
                  rows={2}
                  className={inputCls(false)}
                />
                <div className="flex items-end gap-2 sm:gap-3">
                  <div className="flex-1">
                    <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">Overseer</label>
                    <select
                      value={milestone.overseer}
                      onChange={e => setMilestone({ ...milestone, overseer: e.target.value })}
                      className={inputCls(false)}
                    >
                      <option value="">— Person in charge —</option>
                      {formData.team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={addMilestone}
                    className="flex-shrink-0 inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: BRAND_COLORS.primary }}
                  >
                    <Plus size={13} /> Add
                  </button>
                </div>
                {formData.startDate && formData.endDate && (
                  <p className="text-[10px] sm:text-[11px] text-gray-400">
                    Window: {new Date(formData.startDate).toLocaleDateString()} → {new Date(formData.endDate).toLocaleDateString()}
                  </p>
                )}
              </div>

              {formData.milestones.length > 0 && (
                <ul className="space-y-2">
                  {formData.milestones.map((m, i) => (
                    <li key={m.id} className="flex items-start justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{m.title}</p>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-0.5 text-[10px] sm:text-xs text-gray-400">
                          <span className="flex items-center gap-1"><Calendar size={9} /> {new Date(m.dueDate).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><UserCheck size={9} /> {m.overseerName}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(p => ({ ...p, milestones: p.milestones.filter((_, idx) => idx !== i) }))}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                      >
                        <X size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ── Resources ── */}
            <section className="space-y-3 sm:space-y-4 mt-5 sm:mt-7">
              <SectionHeader icon={Cpu} label="Resources" count={formData.resources.length} />

              <div className="rounded-xl border p-3 sm:p-4 space-y-2 sm:space-y-3 bg-gray-50/60" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                  <select
                    value={resource.type}
                    onChange={e => setResource({ ...resource, type: e.target.value, id: '' })}
                    className={inputCls(false)}
                  >
                    <option value="hardware">Hardware</option>
                    <option value="software">Software</option>
                    <option value="venue">Venue</option>
                  </select>
                  <select
                    value={resource.id}
                    onChange={e => {
                      const sel = availableResources[resource.type].find(r => r.id === e.target.value);
                      setResource({ ...resource, id: e.target.value, name: sel?.name || '' });
                    }}
                    className={inputCls(false)}
                  >
                    <option value="">— Select {resource.type} —</option>
                    {availableResources[resource.type].map(r => (
                      <option key={r.id} value={r.id}>{r.name} ({r.availableQuantity} avail.)</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Qty"
                      value={resource.quantity}
                      onChange={e => setResource({ ...resource, quantity: parseInt(e.target.value) || 1 })}
                      className={inputCls(false)}
                      min="1"
                      max={resource.id ? availableResources[resource.type].find(r => r.id === resource.id)?.availableQuantity : 1}
                    />
                    <button
                      type="button"
                      onClick={addResource}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium text-white transition-opacity hover:opacity-90 whitespace-nowrap"
                      style={{ backgroundColor: BRAND_COLORS.primary }}
                    >
                      <Plus size={13} /> Add
                    </button>
                  </div>
                </div>
              </div>

              {formData.resources.length > 0 && (
                <ul className="space-y-2">
                  {formData.resources.map(r => {
                    const RIcon = RESOURCE_ICON[r.type] || Cpu;
                    return (
                      <li key={r.uniqueId} className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                        <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
                          <RIcon size={12} style={{ color: BRAND_COLORS.primary }} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{r.name}</p>
                          <p className="text-[10px] sm:text-xs text-gray-400">{r.type} · qty {r.quantity}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(p => ({ ...p, resources: p.resources.filter(x => x.uniqueId !== r.uniqueId) }))}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <X size={13} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

          </form>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 px-4 py-3 sm:px-6 sm:py-4 border-t flex-shrink-0" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="project-form"
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            {isSubmitting ? (
              <><Loader2 size={13} className="animate-spin" />{project ? 'Saving…' : 'Creating…'}</>
            ) : (
              <>{project ? 'Save Changes' : 'Create Project'}</>
            )}
          </button>
        </div>

      </div>
    </ModalOverlay>
  );
};

export default ProjectModal;
